// Event Detail & Briefing Modal Component
import type { Mission } from '../types';
import { getCurrentProfile } from '../services/authService';
import { withdrawFromMission } from '../services/missionService';
import { showToast } from '../components/Toast';

export function renderEventDetailModal(mission: Mission): string {
  const profile = getCurrentProfile();
  const currentUid = profile?.uid || '';
  const isEnlisted = mission.enlisted.some(e => e.userId === currentUid);

  let badgeColor = 'badge-project';
  let icon = 'fa-hammer';
  let typeLabel = 'Project';

  if (mission.type === 'social') {
    badgeColor = 'badge-social';
    icon = 'fa-burger';
    typeLabel = 'Social Gathering';
  } else if (mission.type === 'volunteer') {
    badgeColor = 'badge-volunteer';
    icon = 'fa-handshake-angle';
    typeLabel = 'Community Volunteer Mission';
  } else if (mission.type === 'sports') {
    badgeColor = 'badge-sports';
    icon = 'fa-basketball';
    typeLabel = 'Sports Match / Pickup';
  } else if (mission.type === 'gaming' || mission.type === 'sports_gaming') {
    badgeColor = 'badge-gaming';
    icon = 'fa-gamepad';
    typeLabel = 'Gaming Session';
  }

  return `
    <div id="modal-event-detail" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in font-sans">
      <div class="bg-[#0a0c13] border border-white/[0.12] rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-[0_15px_40px_rgba(0,0,0,0.9)] space-y-5 my-8 max-h-[90vh] overflow-y-auto relative">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="flex items-start justify-between gap-3 border-b border-white/[0.08] pb-3">
          <div>
            <div class="flex items-center gap-2 mb-1.5">
              <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-bold ${badgeColor}">
                <i class="fa-solid ${icon}"></i> ${typeLabel}
              </span>
              <span class="text-xs text-slate-400">
                Organized by <button class="view-dad-profile-btn text-cyan-400 hover:text-cyan-300 font-bold hover:underline cursor-pointer" data-dad-uid="${mission.host.userId}">${mission.host.name}</button>
              </span>
            </div>
            <h2 class="text-lg sm:text-xl font-black text-slate-100">${mission.title}</h2>
          </div>
          <button id="close-event-detail-btn" class="text-slate-400 hover:text-white p-1">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <!-- Under Review Banner (if flagged) -->
        ${mission.status === 'under_review' ? `
          <div class="p-3.5 rounded-xl bg-amber-950/40 border border-amber-500/60 text-xs text-amber-200 flex items-start gap-2.5 shadow-[0_0_15px_rgba(255,183,3,0.15)]">
            <i class="fa-solid fa-triangle-exclamation text-amber-400 text-base mt-0.5 flex-shrink-0"></i>
            <div>
              <strong class="text-amber-300 font-bold block mb-0.5">Event Currently Under Review</strong>
              A concern was reported for this event. It has been temporarily unlisted from the public noticeboard while Pops Culture moderators investigate.
            </div>
          </div>
        ` : ''}

        <!-- Mandatory Facilitator Banner -->
        <div class="bg-transparent border border-amber-500/50 rounded-xl p-3 text-xs text-amber-200/90 flex items-start gap-2.5 shadow-[0_0_10px_rgba(255,183,3,0.1)]">
          <i class="fa-solid fa-shield-halved text-amber-400 text-sm mt-0.5 flex-shrink-0"></i>
          <div>
            <strong class="text-amber-300 font-bold block">Community-Organized Event &bull; Self-Directed Peer Undertaking</strong>
            Pops Culture is exclusively a noticeboard. The platform does not inspect sites, verify building permits, or warranty skills. All attendees voluntarily participate at their own risk and provide their own PPE.
          </div>
        </div>

        <!-- Key Details Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-transparent border border-white/[0.08] rounded-xl p-3.5 text-xs">
          <div>
            <span class="text-slate-400 block font-semibold flex items-center justify-between">
              <span>Date & Commitment:</span>
              ${mission.isTentative ? `<span class="text-[10px] text-amber-400 font-bold border border-amber-400/40 px-1.5 py-0.2 rounded">Tentative</span>` : ''}
            </span>
            <span class="text-slate-200 font-medium">${mission.datetime}</span>
            ${mission.isTentative && mission.tentativeDates && mission.tentativeDates.length > 0 ? `
              <div class="mt-1.5 pt-1.5 border-t border-white/[0.06] space-y-1">
                <span class="text-[10px] text-slate-400 font-bold block">Proposed Dates for Attendees:</span>
                <ul class="text-[11px] text-cyan-300 space-y-0.5">
                  ${mission.tentativeDates.map(d => `<li class="flex items-center gap-1.5"><i class="fa-regular fa-clock text-[10px]"></i> ${d}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
          <div>
            <span class="text-slate-400 block font-semibold">Location / Privacy Guardrail:</span>
            ${isEnlisted ? `
              <span class="text-emerald-400 font-bold flex items-center gap-1">
                <i class="fa-solid fa-lock-open text-xs"></i> ${mission.exactAddress}
              </span>
            ` : `
              <span class="text-pink-400 flex items-center gap-1">
                <i class="fa-solid fa-lock text-xs"></i> ${mission.locationMasked}
                ${mission.zipCode ? `<span class="text-slate-400 text-[10px]">(${mission.zipCode})</span>` : ''}
                <span class="text-[10px] text-slate-500 font-normal">(Exact street unlocks on RSVP)</span>
              </span>
            `}
          </div>
          <div>
            <span class="text-slate-400 block font-semibold">Kid Policy:</span>
            <span class="text-slate-200 font-medium">${mission.kidPolicyText}</span>
          </div>
          <div>
            <span class="text-slate-400 block font-semibold">Host Provisions:</span>
            <span class="text-slate-200 font-medium">${mission.provisions || 'None specified'}</span>
          </div>
        </div>

        <!-- Full Briefing -->
        <div class="space-y-1 text-xs">
          <h4 class="font-bold text-cyan-400 uppercase tracking-wider text-[11px]" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.3);">Mission Briefing</h4>
          <p class="text-slate-300 leading-relaxed">${mission.description}</p>
        </div>

        <!-- Skills & Tools -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div class="p-3 bg-transparent rounded-xl border border-white/[0.08] space-y-1.5">
            <h5 class="font-bold text-slate-200 flex items-center gap-1.5">
              <i class="fa-solid fa-screwdriver-wrench text-cyan-400"></i> Peer Skills Welcomed:
            </h5>
            <ul class="space-y-1 text-slate-300 text-[11px]">
              ${mission.skillsNeeded.map(s => `<li class="flex items-center gap-1.5"><i class="fa-solid fa-check text-cyan-400 text-[9px]"></i> ${s}</li>`).join('')}
            </ul>
          </div>
          <div class="p-3 bg-transparent rounded-xl border border-white/[0.08] space-y-1.5">
            <h5 class="font-bold text-slate-200 flex items-center gap-1.5">
              <i class="fa-solid fa-toolbox text-pink-400"></i> Tools Needed On-Site:
            </h5>
            <ul class="space-y-1 text-slate-300 text-[11px]">
              ${mission.toolsNeeded.map(t => `<li class="flex items-center gap-1.5"><i class="fa-solid fa-wrench text-pink-400 text-[9px]"></i> ${t}</li>`).join('')}
            </ul>
          </div>
        </div>

        <!-- Enlisted Attendees Roster -->
        <div class="space-y-2 pt-1">
          <div class="flex items-center justify-between text-xs">
            <h4 class="font-bold text-slate-200 uppercase tracking-wider text-[11px]">
              Enlisted Attendees (${mission.enlisted.length} of ${mission.maxSlots} Dads)
            </h4>
            <span class="text-[11px] text-slate-400">
              ${mission.maxSlots - mission.enlisted.length} openings remaining
            </span>
          </div>
          
          <div class="space-y-1.5 max-h-44 overflow-y-auto">
            ${mission.enlisted.length === 0 ? `
              <div class="text-xs text-slate-500 italic p-3 text-center bg-transparent border border-white/[0.06] rounded-lg">
                No dads enlisted yet. Be the first on the scene!
              </div>
            ` : mission.enlisted.map(att => `
              <div class="flex items-center justify-between p-2.5 rounded-lg bg-transparent border border-white/[0.08] text-xs">
                <button class="view-dad-profile-btn flex items-center gap-2.5 text-left group cursor-pointer hover:opacity-90" data-dad-uid="${att.userId}">
                  <div class="w-7 h-7 rounded-lg bg-transparent border border-cyan-400/40 text-cyan-400 flex items-center justify-center font-bold text-[10px] group-hover:border-cyan-400 shadow-[0_0_6px_rgba(0,240,255,0.2)]">
                    ${att.avatarInitials}
                  </div>
                  <div>
                    <span class="font-bold text-slate-200 group-hover:text-cyan-300 group-hover:underline">${att.name}</span>
                    <span class="text-[10px] ml-1.5 px-2 py-0.5 rounded bg-transparent border border-cyan-400/40 text-cyan-400 font-semibold">${att.role}</span>
                  </div>
                </button>
                <span class="text-[10px] text-slate-400">${att.toolPledged ? `Bringing: ${att.toolPledged}` : 'Bringing hands'}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Actions -->
        <div class="pt-3 border-t border-white/[0.08] flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <button id="detail-back-btn" class="px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-400 hover:text-white border border-transparent hover:border-white/10 transition-colors cursor-pointer">
              Back to Missions
            </button>
            <button id="report-event-btn" class="text-xs text-slate-500 hover:text-amber-400 flex items-center gap-1.5 transition-colors cursor-pointer px-2.5 py-1.5 rounded-lg border border-transparent hover:border-amber-400/20" title="Report a concern regarding this event">
              <i class="fa-solid fa-flag text-[11px]"></i>
              <span class="hidden sm:inline">Report Concern</span>
            </button>
          </div>
          
          <div>
            ${isEnlisted ? `
              <button id="withdraw-btn" class="btn-danger px-4 py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer">
                Cancel My RSVP
              </button>
            ` : `
              <button id="open-enlist-modal-btn" class="btn-primary px-6 py-2.5 rounded-xl text-xs flex items-center gap-2 font-black cursor-pointer">
                <i class="fa-solid fa-hand-fist"></i> Enlist in this Mission
              </button>
            `}
          </div>
        </div>

      </div>
    </div>
  `;
}

export function bindEventDetailModalEvents(
  mission: Mission,
  onClose: () => void,
  onOpenEnlist: (mission: Mission) => void,
  onViewDadProfile?: (dadUid: string) => void,
  onReportConcern?: (mission: Mission) => void
): void {
  document.getElementById('report-event-btn')?.addEventListener('click', () => {
    if (onReportConcern) onReportConcern(mission);
  });
  document.getElementById('close-event-detail-btn')?.addEventListener('click', () => {
    onClose();
  });
  document.getElementById('detail-back-btn')?.addEventListener('click', () => {
    onClose();
  });

  document.getElementById('open-enlist-modal-btn')?.addEventListener('click', () => {
    onOpenEnlist(mission);
  });

  document.getElementById('withdraw-btn')?.addEventListener('click', async () => {
    const profile = getCurrentProfile();
    if (profile) {
      await withdrawFromMission(mission.id, profile.uid);
      showToast('You have cancelled your RSVP for this mission.', 'info');
      onClose();
    }
  });

  // Dad Profile Click Trigger
  document.querySelectorAll('.view-dad-profile-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const dadUid = (btn as HTMLElement).dataset.dadUid;
      if (dadUid && onViewDadProfile) {
        onViewDadProfile(dadUid);
      }
    });
  });

  // Backdrop click dismiss
  const modal = document.getElementById('modal-event-detail');
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      onClose();
    }
  });
}
